import { useMemo, useState } from 'react'
import { PILARES } from '../../shared/schema'
import { createRecord, useRecords } from '../lib/api'
import { useSession } from '../lib/auth'
import { Link } from '../lib/router'
import { fmtDay, today, ymd } from '../lib/format'
import { IDEAS, type Idea } from '../lib/ideas'
import { pillarColor } from './Marketing'
import { Avatar, Badge, Button, copyText, Drawer, Empty, Icon, PageHeader, Segmented, Star, useToast } from '../components/ui'

const FORMATOS = ['Reel', 'Carrusel', 'Historia', 'Post'] as const

export function Ideas() {
  const posts = useRecords('marketing').data
  const [pilar, setPilar] = useState('')
  const [formato, setFormato] = useState('')
  const [quien, setQuien] = useState('')
  const [semana, setSemana] = useState<'todas' | '1' | '2' | '3' | '4'>('todas')
  const [q, setQ] = useState('')
  const [open, setOpen] = useState<Idea | null>(null)

  const usadas = useMemo(() => new Set(posts.map(p => String(p.titulo ?? '').trim().toLowerCase())), [posts])
  const usada = (i: Idea) => usadas.has(i.titulo.trim().toLowerCase())

  const list = IDEAS.filter(i =>
    (!pilar || i.pilar === pilar) && (!formato || i.formato === formato) &&
    (!quien || i.quien === quien || (quien !== 'Las dos' && i.quien === 'Las dos')) &&
    (semana === 'todas' || String(i.semana) === semana) &&
    (!q || [i.titulo, i.hook, i.desarrollo, i.decir].join(' ').toLowerCase().includes(q.toLowerCase())))

  const enCalendario = IDEAS.filter(usada).length

  return (
    <div className="page ideas">
      <PageHeader eyebrow="Banco de ideas" title="Ideas de contenido" subtitle="30 ideas para @revela.real.estate con qué grabar, qué decir y cómo diseñar cada pieza. Tocá una para verla completa y pasarla al calendario."
        actions={<div className="ideas-progress"><strong>{enCalendario}</strong><span>de {IDEAS.length} ya están en el calendario</span></div>} />

      <div className="filters">
        <div className="pillar-filter" role="group" aria-label="Filtrar por pilar">
          {PILARES.map(p => {
            const on = pilar === p
            return (
              <button key={p} type="button" className={`pillar-chip ${on ? 'on' : ''} ${pilar && !on ? 'dim' : ''}`} style={{ ['--pc' as any]: pillarColor(p) }}
                onClick={() => setPilar(on ? '' : p)} aria-pressed={on}>
                <span className="pc-dot" />{p}<span className="chip-n">{IDEAS.filter(i => i.pilar === p).length}</span>
              </button>
            )
          })}
        </div>
        <div className="toolbar">
          <Segmented value={semana} onChange={setSemana} options={[
            { value: 'todas', label: 'Todo el mes' }, { value: '1', label: 'Semana 1' }, { value: '2', label: 'Semana 2' }, { value: '3', label: 'Semana 3' }, { value: '4', label: 'Semana 4' },
          ]} />
          <div className="toolbar-right">
            <div className="search-input"><Icon name="search" size={16} /><input id="ideas-q" type="search" placeholder="Buscar idea" value={q} onChange={e => setQ(e.target.value)} aria-label="Buscar ideas" /></div>
            <select id="ideas-formato" value={formato} onChange={e => setFormato(e.target.value)} aria-label="Formato"><option value="">Todo formato</option>{FORMATOS.map(f => <option key={f}>{f}</option>)}</select>
            <select id="ideas-quien" value={quien} onChange={e => setQuien(e.target.value)} aria-label="Quién"><option value="">Las dos socias</option><option value="Leticia">Para Leticia</option><option value="Yesica">Para Yesica</option><option value="Las dos">Juntas</option></select>
          </div>
        </div>
      </div>

      {list.length ? (
        <div className="idea-grid">
          {list.map(i => (
            <article key={i.id} className={`idea-card ${usada(i) ? 'is-used' : ''}`} style={{ ['--pc' as any]: pillarColor(i.pilar) }}
              onClick={() => setOpen(i)} onKeyDown={e => e.key === 'Enter' && setOpen(i)} tabIndex={0}>
              <div className="idea-top">
                <span className="idea-pillar">{i.pilar}</span>
                <span className="idea-week">Semana {i.semana}</span>
              </div>
              <h3>{i.titulo}</h3>
              <p className="idea-hook">“{i.hook.replace(/^(Texto( en pantalla)?|Placa 1|Frase en la imagen|Leticia a cámara): ?/i, '').replace(/^"|"$/g, '')}”</p>
              <div className="idea-foot">
                <Badge tone="sand">{i.formato}</Badge>
                <span className="idea-angle">{i.angulo}</span>
                {i.quien === 'Las dos' ? <span className="avatars"><Avatar name="Leticia" /><Avatar name="Yesica" /></span> : <Avatar name={i.quien} />}
              </div>
              {usada(i) && <span className="idea-used"><Icon name="check" size={12} /> En el calendario</span>}
            </article>
          ))}
        </div>
      ) : <Empty title="Ninguna idea coincide" text="Probá con otro filtro." />}

      {open && <IdeaDetail idea={open} usada={usada(open)} onClose={() => setOpen(null)} posts={posts} />}
    </div>
  )
}

function nextFreeDay(posts: any[]) {
  const taken = new Set(posts.filter(p => (p.tipo || 'Publicación') === 'Publicación').map(p => p.fecha))
  const d = new Date(); d.setDate(d.getDate() + 1)
  for (let k = 0; k < 60 && taken.has(ymd(d)); k++) d.setDate(d.getDate() + 1)
  return ymd(d)
}

function IdeaDetail({ idea, usada, onClose, posts }: { idea: Idea; usada: boolean; onClose: () => void; posts: any[] }) {
  const toast = useToast()
  const { name } = useSession()
  const [fecha, setFecha] = useState(() => nextFreeDay(posts))
  const [busy, setBusy] = useState(false)
  const [hecho, setHecho] = useState(false)

  const guion = [
    'QUÉ GRABAR / PREPARAR', ...idea.grabar.map(g => `• ${g}`), '',
    'QUÉ DECIR', idea.decir, '',
    'DISEÑO', idea.diseno,
  ].join('\n')

  async function agregar() {
    setBusy(true)
    try {
      await createRecord('marketing', {
        titulo: idea.titulo, tipo: 'Publicación', pilar: idea.pilar, formato: idea.formato, estado: 'Idea', fecha,
        socia: idea.quien === 'Las dos' ? name : idea.quien,
        idea: `${idea.hook}\n\n${idea.desarrollo}`, guion, copy: idea.copy.startsWith('(') ? '' : idea.copy,
        etiquetas: ['Reutilizable'],
      })
      setHecho(true)
      toast(`Agregada al calendario para el ${fmtDay(fecha)}`)
    } catch (e: any) { toast(e.message, 'error') } finally { setBusy(false) }
  }

  const copiar = async (texto: string, que: string) => {
    const ok = await copyText(texto)
    toast(ok ? `${que} copiado` : 'No se pudo copiar; seleccioná el texto a mano', ok ? 'ok' : 'error')
  }

  return (
    <Drawer open onClose={onClose} eyebrow={`${idea.pilar} · ${idea.angulo} · ${idea.formato}`} title={idea.titulo} wide
      footer={
        <>
          <div className="foot-left">
            <Button size="sm" variant="quiet" icon="copy" onClick={() => copiar(`${idea.titulo}\n\n${idea.hook}\n\n${idea.desarrollo}\n\n${guion}\n\nCOPY\n${idea.copy}`, 'Todo')}>Copiar todo</Button>
          </div>
          <div className="foot-right">
            {hecho || usada ? (
              <Link to="/marketing" className="btn btn-ghost btn-md"><Icon name="calendar" size={17} /><span>Ver en el calendario</span></Link>
            ) : (
              <>
                <div className="idea-foot-form">
                  <input id="idea-fecha" type="date" min={today()} value={fecha} onChange={e => setFecha(e.target.value)} aria-label="Fecha de publicación" />
                  <Button variant="accent" icon="plus" busy={busy} onClick={agregar}>Agregar al calendario</Button>
                </div>
              </>
            )}
          </div>
        </>
      }>
      <div className="idea-detail" style={{ ['--pc' as any]: pillarColor(idea.pilar) }}>
        <section className="idea-hook-box">
          <span className="ctx-label">Arranque (los primeros 3 segundos)</span>
          <p>{idea.hook}</p>
        </section>
        <section>
          <span className="ctx-label">Cómo se desarrolla</span>
          <p>{idea.desarrollo}</p>
          <p className="muted small">Quién: {idea.quien === 'Las dos' ? 'Leticia y Yesica' : idea.quien}{idea.quien !== 'Leticia' && ' · Yesica sin mostrar la cara: voz en off, manos o de espaldas'}</p>
        </section>
        <div className="idea-cols">
          <section className="idea-block">
            <h3><Icon name="image" size={16} /> Qué grabar o preparar</h3>
            <ul className="idea-list">{idea.grabar.map((g, k) => <li key={k}>{g}</li>)}</ul>
          </section>
          <section className="idea-block">
            <h3><Icon name="chat" size={16} /> Qué decir</h3>
            <p className="pre">{idea.decir}</p>
            <Button size="sm" variant="quiet" icon="copy" onClick={() => copiar(idea.decir, 'Texto')}>Copiar texto</Button>
          </section>
        </div>
        <section className="idea-block">
          <h3><Star size={14} /> Diseño de la pieza</h3>
          <p>{idea.diseno}</p>
          <div className="brand-swatches" aria-label="Paleta de REVELA">
            {[['Crema', '#F7F4EE'], ['Arena', '#DBCBB0'], ['Oliva', '#5A6B5C'], ['Negro', '#000000'], ['Terracota', '#C46D51']].map(([n, c]) => (
              <button key={c} type="button" className="swatch" onClick={() => copiar(c, `${n} ${c}`)} title={`Copiar ${c}`}><span style={{ background: c }} />{n}</button>
            ))}
          </div>
        </section>
        <section className="idea-block">
          <h3><Icon name="edit" size={16} /> Texto de la publicación</h3>
          <p className="pre">{idea.copy}</p>
          {!idea.copy.startsWith('(') && <Button size="sm" variant="quiet" icon="copy" onClick={() => copiar(idea.copy, 'Copy')}>Copiar copy</Button>}
        </section>
      </div>
    </Drawer>
  )
}
